import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Wallet, Banknote, CreditCard, Smartphone, Building, Send, Plus, X, TrendingUp, Trash2, ArrowDown, ArrowUp, Receipt } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

// Metodo → colore badge
const METHOD_META = {
  contanti:  { label: "Contanti",  color: "#10b981", icon: Banknote, bg: "bg-emerald-500/15", border: "border-emerald-500/30", text: "text-emerald-300" },
  pos:       { label: "POS",       color: "#3b82f6", icon: CreditCard, bg: "bg-blue-500/15", border: "border-blue-500/30", text: "text-blue-300" },
  sumup:     { label: "SumUp",     color: "#0ea5e9", icon: Smartphone, bg: "bg-sky-500/15", border: "border-sky-500/30", text: "text-sky-300" },
  stripe:    { label: "Stripe",    color: "#8b5cf6", icon: CreditCard, bg: "bg-violet-500/15", border: "border-violet-500/30", text: "text-violet-300" },
  bonifico:  { label: "Bonifico",  color: "#f59e0b", icon: Building, bg: "bg-amber-500/15", border: "border-amber-500/30", text: "text-amber-300" },
  paypal:    { label: "PayPal",    color: "#f43f5e", icon: Send, bg: "bg-rose-500/15", border: "border-rose-500/30", text: "text-rose-300" },
  altro:     { label: "Altro",     color: "#6b7280", icon: Wallet, bg: "bg-white/[0.05]", border: "border-white/15", text: "text-white/70" },
};

const DAY_LABELS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const CAT_SPESE = [
  "carburante", "manutenzione", "ricambi", "officina", "lavaggio",
  "bollo", "assicurazione", "leasing", "ufficio", "marketing",
  "stipendi", "tasse", "utenze", "fornitori", "altro",
];

function addDays(iso, days) {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function WeeklyCash() {
  const [weekOffset, setWeekOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [formTipo, setFormTipo] = useState("incasso"); // incasso | spesa
  const [form, setForm] = useState({
    data: new Date().toISOString().slice(0, 10),
    importo: 0,
    metodo: "contanti",
    pagato_da: "",
    operatore: "",
    causale: "",
    categoria_spesa: "carburante",
  });

  const openForm = (tipo) => {
    setFormTipo(tipo);
    setForm((f) => ({ ...f, importo: 0, pagato_da: "", causale: "" }));
    setFormOpen(true);
  };

  const load = () => {
    setLoading(true);
    api.get("/cash/weekly", { params: { week_offset: weekOffset } }).then((r) => {
      setData(r.data);
      setLoading(false);
    });
  };
  useEffect(load, [weekOffset]);

  const dayKeys = useMemo(() => {
    if (!data) return [];
    const out = [];
    let d = data.monday;
    for (let i = 0; i < 7; i++) { out.push(d); d = addDays(d, 1); }
    return out;
  }, [data]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.importo || +form.importo <= 0) return toast.error("Importo non valido");
    if (formTipo === "incasso" && !form.pagato_da) return toast.error("Inserisci chi ha pagato");
    if (formTipo === "spesa" && !form.causale && !form.pagato_da) return toast.error("Inserisci causale o fornitore");
    try {
      const payload = { ...form, importo: +form.importo, tipo: formTipo };
      await api.post("/cash", payload);
      toast.success(`${formTipo === "spesa" ? "Spesa" : "Incasso"} di € ${form.importo} registrato`);
      setForm({ ...form, importo: 0, pagato_da: "", causale: "" });
      setFormOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Eliminare questa transazione?")) return;
    await api.delete(`/cash/${id}`);
    toast.success("Eliminata");
    load();
  };

  const today = new Date().toISOString().slice(0, 10);

  if (loading && !data) {
    return <div className="glass rounded-2xl p-8 text-center text-white/40">Caricamento cassa…</div>;
  }

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="glass rounded-2xl overflow-hidden border border-white/10" data-testid="weekly-cash">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
            <Wallet size={16} className="text-emerald-300" />
          </div>
          <div>
            <div className="font-display text-lg font-bold uppercase tracking-tight">Contabilità di Cassa Settimanale</div>
            <div className="text-[10px] text-white/40 uppercase tracking-widest font-mono">
              {data ? `${new Date(data.monday).toLocaleDateString("it-IT")} → ${new Date(data.sunday).toLocaleDateString("it-IT")}` : ""}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setWeekOffset(weekOffset - 1)} data-testid="week-prev" className="px-3 py-1.5 text-xs rounded-full bg-white/[0.04] border border-white/10 hover:bg-white/10">◂ Prec.</button>
          <button onClick={() => setWeekOffset(0)} className="px-3 py-1.5 text-xs rounded-full bg-white/[0.04] border border-white/10 hover:bg-white/10">Oggi</button>
          <button onClick={() => setWeekOffset(weekOffset + 1)} data-testid="week-next" className="px-3 py-1.5 text-xs rounded-full bg-white/[0.04] border border-white/10 hover:bg-white/10">Succ. ▸</button>
          <button onClick={() => openForm("incasso")} data-testid="cash-add"
            className="px-4 py-2 rounded-full text-xs inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-black font-bold transition-colors">
            <ArrowDown size={12} /> Incasso
          </button>
          <button onClick={() => openForm("spesa")} data-testid="expense-add"
            className="px-4 py-2 rounded-full text-xs inline-flex items-center gap-1.5 bg-[#ff0033] hover:bg-[#ff2255] text-white font-bold transition-colors">
            <ArrowUp size={12} /> Spesa
          </button>
        </div>
      </div>

      {/* Cumulo + Breakdown metodo */}
      <div className="px-6 py-4 border-b border-white/5 bg-gradient-to-r from-[#ff0033]/[0.04] via-emerald-500/[0.04] to-transparent">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3">
          <div className="p-3 rounded-xl bg-emerald-500/[0.06] border border-emerald-500/20">
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 inline-flex items-center gap-1"><ArrowDown size={10} /> Incassi settimana</div>
            <div className="font-display text-2xl font-extrabold text-emerald-300 mt-1 font-mono" data-testid="weekly-incassi">
              € {(data?.cumulo_incassi || 0).toFixed(2)}
            </div>
          </div>
          <div className="p-3 rounded-xl bg-[#ff0033]/[0.06] border border-[#ff0033]/20">
            <div className="text-[10px] uppercase tracking-widest text-[#ff5577] inline-flex items-center gap-1"><ArrowUp size={10} /> Spese settimana</div>
            <div className="font-display text-2xl font-extrabold text-[#ff5577] mt-1 font-mono" data-testid="weekly-spese">
              € {(data?.cumulo_spese || 0).toFixed(2)}
            </div>
          </div>
          <div className={`p-3 rounded-xl ${(data?.cumulo_saldo || 0) >= 0 ? "bg-emerald-500/[0.10] border-emerald-500/30" : "bg-[#ff0033]/[0.10] border-[#ff0033]/30"} border md:col-span-2`}>
            <div className={`text-[10px] uppercase tracking-widest inline-flex items-center gap-1 ${(data?.cumulo_saldo || 0) >= 0 ? "text-emerald-300" : "text-[#ff5577]"}`}>
              <Wallet size={10} /> Saldo netto cassa (Incassi − Spese)
            </div>
            <div className={`font-display text-3xl font-extrabold mt-1 font-mono ${(data?.cumulo_saldo || 0) >= 0 ? "text-emerald-300" : "text-[#ff5577]"}`} data-testid="weekly-saldo">
              € {(data?.cumulo_saldo || 0).toFixed(2)}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(data?.by_method || {}).map(([m, total]) => {
            const meta = METHOD_META[m] || METHOD_META.altro;
            const Ico = meta.icon;
            return (
              <div key={m} className={`px-3 py-1.5 rounded-lg border ${meta.bg} ${meta.border} ${meta.text}`}>
                <div className="flex items-center gap-2">
                  <Ico size={10} />
                  <span className="text-[9px] uppercase tracking-widest">{meta.label}</span>
                  <span className="font-mono text-xs font-bold">€ {total.toFixed(0)}</span>
                </div>
              </div>
            );
          })}
          {Object.entries(data?.by_categoria_spesa || {}).map(([c, total]) => (
            <div key={c} className="px-3 py-1.5 rounded-lg border bg-[#ff0033]/10 border-[#ff0033]/25 text-[#ff5577]">
              <div className="flex items-center gap-2">
                <Receipt size={10} />
                <span className="text-[9px] uppercase tracking-widest">{c}</span>
                <span className="font-mono text-xs font-bold">€ {total.toFixed(0)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Grid 7 giorni */}
      <div className="grid grid-cols-1 md:grid-cols-7 gap-px bg-white/5">
        {dayKeys.map((d, i) => {
          const day = data?.days?.[d] || { incassi: [], spese: [] };
          const t = data?.totals?.[d] || { incassi: 0, spese: 0, saldo: 0 };
          const isSabato = i === 5;
          const isToday = d === today;
          return (
            <div key={d} className={`bg-[#0a0a0a] p-3 min-h-[280px] flex flex-col ${isToday ? "ring-1 ring-[#ff0033] ring-inset" : ""}`} data-testid={`day-${d}`}>
              {/* Header giorno */}
              <div className="flex items-center justify-between mb-2 pb-2 border-b border-white/5">
                <div>
                  <div className={`text-[10px] uppercase tracking-widest font-bold ${isSabato ? "text-emerald-300" : "text-white/50"}`}>{DAY_LABELS[i]}</div>
                  <div className="text-[10px] text-white/35 font-mono">{new Date(d).getDate()}/{new Date(d).getMonth() + 1}</div>
                </div>
                {isToday && <span className="text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#ff0033]/20 text-[#ff5577]">Oggi</span>}
              </div>

              {/* Entries del giorno */}
              <div className="space-y-1.5 flex-1 overflow-y-auto max-h-[200px] pr-1">
                {day.incassi.length === 0 && day.spese.length === 0 && (
                  <div className="text-[10px] text-white/25 text-center py-4 italic">—</div>
                )}
                {/* INCASSI */}
                {day.incassi.map((e) => {
                  const meta = METHOD_META[e.metodo] || METHOD_META.altro;
                  const Ico = meta.icon;
                  return (
                    <motion.div key={e.id} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                      className={`group p-2 rounded-lg border ${meta.bg} ${meta.border} relative`} data-testid={`entry-${e.id}`}>
                      <div className="flex items-center justify-between gap-1">
                        <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold ${meta.text}`}>
                          <ArrowDown size={7} /><Ico size={8} /> {meta.label}
                        </span>
                        <button onClick={() => deleteEntry(e.id)} className="opacity-0 group-hover:opacity-100 text-white/40 hover:text-[#ff0033] transition-opacity">
                          <Trash2 size={9} />
                        </button>
                      </div>
                      <div className="font-mono font-bold text-base mt-0.5" style={{ color: meta.color }}>+€ {(+e.importo).toFixed(2)}</div>
                      <div className="text-[9px] text-white/60 truncate"><span className="text-white/40">da:</span> <b>{e.pagato_da}</b></div>
                      {e.operatore && <div className="text-[8px] text-white/40 truncate">op. {e.operatore}</div>}
                      {e.causale && <div className="text-[8px] text-white/30 truncate italic">{e.causale}</div>}
                    </motion.div>
                  );
                })}
                {/* SPESE */}
                {day.spese.map((e) => (
                  <motion.div key={e.id} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                    className="group p-2 rounded-lg border bg-[#ff0033]/[0.08] border-[#ff0033]/25 relative" data-testid={`expense-${e.id}`}>
                    <div className="flex items-center justify-between gap-1">
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold text-[#ff5577]">
                        <ArrowUp size={7} /><Receipt size={8} /> {e.categoria_spesa || "Spesa"}
                      </span>
                      <button onClick={() => deleteEntry(e.id)} className="opacity-0 group-hover:opacity-100 text-white/40 hover:text-[#ff0033] transition-opacity">
                        <Trash2 size={9} />
                      </button>
                    </div>
                    <div className="font-mono font-bold text-base mt-0.5 text-[#ff5577]">−€ {(+e.importo).toFixed(2)}</div>
                    {(e.pagato_da || e.causale) && (
                      <div className="text-[9px] text-white/60 truncate">{e.pagato_da || e.causale}</div>
                    )}
                    {e.operatore && <div className="text-[8px] text-white/40 truncate">op. {e.operatore}</div>}
                  </motion.div>
                ))}
              </div>

              {/* Totale giorno: Incassi - Spese = Saldo */}
              <div className={`mt-2 pt-2 border-t ${isSabato ? "border-emerald-500/30" : "border-white/5"} space-y-0.5`}>
                <div className="flex justify-between text-[9px]"><span className="text-emerald-300/70">In</span><span className="font-mono text-emerald-300/90">+€ {t.incassi.toFixed(2)}</span></div>
                <div className="flex justify-between text-[9px]"><span className="text-[#ff5577]/70">Out</span><span className="font-mono text-[#ff5577]/90">−€ {t.spese.toFixed(2)}</span></div>
                <div className="flex justify-between border-t border-white/5 pt-0.5">
                  <span className="text-[9px] uppercase tracking-widest text-white/45">Saldo</span>
                  <span className={`font-display font-extrabold text-base font-mono ${t.saldo >= 0 ? "text-emerald-300" : "text-[#ff5577]"}`} data-testid={`saldo-${d}`}>
                    € {t.saldo.toFixed(2)}
                  </span>
                </div>
                {/* SABATO: cumulo settimanale */}
                {isSabato && (
                  <div className="mt-2 pt-2 border-t border-emerald-500/30 rounded-lg bg-emerald-500/[0.06] p-2">
                    <div className="text-[8px] uppercase tracking-widest text-emerald-300 inline-flex items-center gap-1">
                      <TrendingUp size={8} /> Cumulo Settimana
                    </div>
                    <div className={`font-display font-extrabold text-base font-mono ${(data?.cumulo_saldo || 0) >= 0 ? "text-emerald-300" : "text-[#ff5577]"}`} data-testid="cumulo-sabato">
                      € {(data?.cumulo_saldo || 0).toFixed(2)}
                    </div>
                    <div className="text-[8px] text-white/35 mt-0.5">Netto: +€{(data?.cumulo_incassi||0).toFixed(0)} −€{(data?.cumulo_spese||0).toFixed(0)}</div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* === FORM RAPIDO REGISTRA INCASSO === */}
      <AnimatePresence>
      {formOpen && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setFormOpen(false)}>
          <motion.form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
            className={`w-full max-w-2xl bg-[#0a0a0a] border border-white/10 rounded-3xl overflow-hidden ${formTipo === "spesa" ? "shadow-[0_0_120px_-20px_#ff0033]" : "shadow-[0_0_120px_-20px_#10b981]"}`}
            data-testid="cash-form">
            <div className={`flex items-center justify-between px-6 py-4 border-b border-white/5 ${formTipo === "spesa" ? "bg-gradient-to-r from-[#ff0033]/[0.08]" : "bg-gradient-to-r from-emerald-500/[0.08]"} via-transparent to-transparent`}>
              <div>
                <div className="font-display text-lg font-bold inline-flex items-center gap-2">
                  {formTipo === "spesa" ? (
                    <><Receipt size={16} className="text-[#ff5577]" /> Registra Spesa</>
                  ) : (
                    <><Wallet size={16} className="text-emerald-300" /> Registra Incasso</>
                  )}
                </div>
                <div className="text-[10px] text-white/40 mt-0.5">{formTipo === "spesa" ? "Sottrae dal saldo giornaliero" : "Aggiunge al saldo giornaliero"}</div>
              </div>
              <button type="button" onClick={() => setFormOpen(false)} className="p-2 rounded-full hover:bg-white/10"><X size={16} /></button>
            </div>

            <div className="p-6 space-y-4">
              {/* Toggle Incasso/Spesa */}
              <div className="flex gap-2">
                <button type="button" onClick={() => setFormTipo("incasso")} data-testid="form-tipo-incasso"
                  className={`flex-1 py-2 rounded-lg text-xs uppercase tracking-widest font-bold inline-flex items-center justify-center gap-2 transition-all ${formTipo === "incasso" ? "bg-emerald-500/15 border border-emerald-500/40 text-emerald-300" : "bg-white/[0.02] border border-white/10 text-white/50"}`}>
                  <ArrowDown size={12} /> Incasso (+)
                </button>
                <button type="button" onClick={() => setFormTipo("spesa")} data-testid="form-tipo-spesa"
                  className={`flex-1 py-2 rounded-lg text-xs uppercase tracking-widest font-bold inline-flex items-center justify-center gap-2 transition-all ${formTipo === "spesa" ? "bg-[#ff0033]/15 border border-[#ff0033]/40 text-[#ff5577]" : "bg-white/[0.02] border border-white/10 text-white/50"}`}>
                  <ArrowUp size={12} /> Spesa (−)
                </button>
              </div>

              {/* Giorno + Importo */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/40">Giorno</label>
                  <input type="date" required value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })}
                    data-testid="cash-data"
                    className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-emerald-500/60 focus:outline-none" />
                </div>
                <div>
                  <label className={`text-[10px] uppercase tracking-widest ${formTipo === "spesa" ? "text-[#ff5577]" : "text-emerald-300"}`}>Importo (€) *</label>
                  <input type="number" step="0.01" required value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value })}
                    data-testid="cash-importo"
                    placeholder="0,00" autoFocus
                    className={`mt-1 w-full ${formTipo === "spesa" ? "bg-[#ff0033]/[0.06] border-[#ff0033]/30" : "bg-emerald-500/[0.06] border-emerald-500/30"} border rounded-lg px-3 py-2 text-lg font-mono font-bold focus:outline-none`} />
                </div>
              </div>

              {/* Categoria spesa (solo per spese) */}
              {formTipo === "spesa" && (
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#ff5577] mb-2 block">Categoria spesa</label>
                  <select value={form.categoria_spesa} onChange={(e) => setForm({ ...form, categoria_spesa: e.target.value })}
                    data-testid="form-categoria-spesa"
                    className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none">
                    {CAT_SPESE.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
              )}

              {/* Metodo (visual selector) */}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40 mb-2 block">{formTipo === "spesa" ? "Pagato con" : "Metodo / Dove sono finiti"}</label>
                <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
                  {Object.entries(METHOD_META).map(([k, meta]) => {
                    const Ico = meta.icon;
                    const active = form.metodo === k;
                    return (
                      <button key={k} type="button" onClick={() => setForm({ ...form, metodo: k })}
                        data-testid={`method-${k}`}
                        className={`p-2 rounded-lg border transition-all text-center ${active ? `${meta.bg} ${meta.border} ${meta.text}` : "bg-white/[0.02] border-white/10 text-white/50 hover:border-white/25"}`}>
                        <Ico size={14} className="mx-auto" />
                        <div className="text-[9px] uppercase tracking-wider mt-1">{meta.label}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/40">
                    {formTipo === "spesa" ? "Fornitore / Pagato a" : "Chi ha pagato"} *
                  </label>
                  <input value={form.pagato_da} onChange={(e) => setForm({ ...form, pagato_da: e.target.value })}
                    data-testid="cash-pagato-da"
                    placeholder={formTipo === "spesa" ? "Es. Esso Imola" : "Es. Mario Rossi"}
                    className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-emerald-500/60 focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/40">Operatore</label>
                  <input value={form.operatore} onChange={(e) => setForm({ ...form, operatore: e.target.value })}
                    data-testid="cash-operatore"
                    placeholder="Default: te stesso"
                    className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-emerald-500/60 focus:outline-none" />
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Causale / Note</label>
                <input value={form.causale} onChange={(e) => setForm({ ...form, causale: e.target.value })}
                  placeholder={formTipo === "spesa" ? "Es. rifornimento Fiat 500" : "Es. Acconto noleggio"}
                  className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-emerald-500/60 focus:outline-none" />
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/5 bg-black/40">
              <button type="button" onClick={() => setFormOpen(false)} className="px-5 py-2 rounded-full text-sm bg-white/[0.04] border border-white/10 hover:bg-white/10">Annulla</button>
              <button type="submit" data-testid="cash-submit"
                className={`px-6 py-2 rounded-full text-sm font-bold inline-flex items-center gap-2 transition-colors ${formTipo === "spesa" ? "bg-[#ff0033] hover:bg-[#ff2255] text-white" : "bg-emerald-500 hover:bg-emerald-400 text-black"}`}>
                <Plus size={13} /> Registra {formTipo === "spesa" ? "Spesa" : "Incasso"}
              </button>
            </div>
          </motion.form>
        </div>
      )}
      </AnimatePresence>
    </motion.div>
  );
}
